import {
  PermissionContext,
  permissionsOperations,
} from '@ardoq/access-control';
import { scenarioAccessControlInterface } from 'resourcePermissions/accessControlHelpers/scenarioAccessControlInterface';
import {
  APIComponentAttributes,
  APIEntityType,
  APIFieldAttributes,
  APIFieldType,
  APIReferenceAttributes,
  APIReferenceType,
  ArdoqId,
  NUMBER_FORMAT_PERCENTAGE,
} from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { activeScenarioOperations } from '@ardoq/core';
import { type EnhancedScopeData, ScenarioModeState } from '@ardoq/data-model';
import { isDateRangeFieldType } from '@ardoq/date-range';
import {
  formatNumber,
  Locale,
  percentageEditValueToStoredValue,
  percentageStoredValueToEditValue,
} from '@ardoq/locale';
import { t, type I18nLocale } from '@ardoq/i18n';
import { type EditableEntityPropertiesToCompare } from './editableEntityProperties';
import {
  componentAttributesMap,
  defaultAttributesMap,
  FieldType,
  fieldTypeMap,
  getEntityById,
  getEntityModelAndTypeID,
  getFieldLabel,
  getModelTypeDictionaryKey,
  readRawValue,
  referenceAttributesMap,
} from '@ardoq/renderers';
import { componentUtils, referenceUtils } from '@ardoq/scope-data';
import {
  propertiesNotEditableAcrossMultipleWorkspaces as componentPropertiesNotEditableAcrossMultipleWorkspaces,
  defaultComponentAttributeNames,
  defaultMultiComponentAttributeNames,
} from 'appModelStateEdit/propertiesEditor/componentPropertiesEditor/consts';
import {
  customPropertyOptionGetterMap as customComponentPropertyOptionGetterMap,
  defaultPropertyOptionGetterMap as defaultComponentPropertyOptionGetterMap,
} from 'appModelStateEdit/propertiesEditor/componentPropertiesEditor/utils';
import {
  type ComponentStyleImageOption,
  getDefaultProperties as getDefaultComponentStyleProperties,
} from 'appModelStateEdit/propertiesEditor/componentStylePropertiesEditor/utils';
import {
  getCalculatedFieldEditMessage,
  getConfidentialFieldEditMessage,
  getCrossWorkspaceEditMessage,
  getLocallyDerivedFieldEditMessage,
  getLockedMessageText,
  getManagedFieldEditMessage,
} from 'appModelStateEdit/propertiesEditor/consts';
import { splitDateRangeProperties } from 'appModelStateEdit/propertiesEditor/fieldUtils';
import {
  getMergedCustomMultiEntityProperties,
  getMergedMultiEntityProperties,
} from 'appModelStateEdit/propertiesEditor/multiUtils';
import {
  defaultMultiReferenceAttributeNames,
  defaultReferenceAttributeNames,
  propertiesNotEditableAcrossMultipleWorkspaces as referencePropertiesNotEditableAcrossMultipleWorkspaces,
} from 'appModelStateEdit/propertiesEditor/referencePropertiesEditor/consts';
import {
  customPropertyOptionGetterMap as customReferencePropertyOptionGetterMap,
  defaultPropertyOptionGetterMap as defaultReferencePropertyOptionGetterMap,
  getTripleWarningProperty,
  isCardinalityAttributeAllowed,
  sourceTargetIdTupleToNameTuple,
  toSourceTargetIdTuple,
} from 'appModelStateEdit/propertiesEditor/referencePropertiesEditor/utils';
import { getEntityTags } from 'appModelStateEdit/propertiesEditor/tagUtils';
import {
  EditorProperty,
  PropertyGroup,
} from 'appModelStateEdit/propertiesEditor/types';
import { DirtyAttributes, EditableEntityType } from 'appModelStateEdit/types';
import { intersection, isEqual, truncate, uniq } from 'lodash';
import { componentAccessControlOperation } from 'resourcePermissions/accessControlHelpers/component';
import { referenceAccessControlOperation } from 'resourcePermissions/accessControlHelpers/reference';
import { workspaceAccessControlInterface } from 'resourcePermissions/accessControlHelpers/workspace';
import {
  getDisplayTextErrorMessage,
  getValidationErrorMessages,
  isDisplayTextRequiredForReference,
  selectedValueExistInOptionsValidator,
} from 'scopeData/editors/validators';
import { getActiveScenarioState } from 'streams/activeScenario/activeScenario$';
import { getFeatureContext } from 'streams/featureContext$';
import { hasOwnProperty } from 'utils/objectUtils';
import { fieldOps } from '../../models/utils/fieldOps';
import { IntendedRefTypes } from '../../utils/metamodelConstraints';

import {
  CustomPropertyOptionGetterMap,
  DefaultPropertyOptionGetterMap,
} from './optionGetterTypes';
import { lockOperations } from 'lockedComponents/lockOperations';

type ManagedDefinition = { seenInLastImport?: boolean };

export const isReferenceExternallyManaged = (
  enhancedScopeData: EnhancedScopeData,
  referenceID: ArdoqId
): boolean => {
  const reference = enhancedScopeData.referencesById[referenceID];
  if (!reference) {
    return false;
  }
  const workspace = enhancedScopeData.workspacesById[reference.rootWorkspace];
  const jobIds = (workspace?.integrations ?? []).map(
    integration => integration._id
  );
  const managedData: Record<string, ManagedDefinition> =
    (
      reference['ardoq-persistent'] as {
        managed?: Record<string, ManagedDefinition>;
      }
    )?.managed ?? {};
  return jobIds
    .map(jobId => managedData[jobId])
    .some(definition => definition?.seenInLastImport);
};

const isManaged = (
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  entityID: ArdoqId,
  enhancedScopeData: EnhancedScopeData
): boolean => {
  if (entityType === APIEntityType.REFERENCE) {
    return isReferenceExternallyManaged(enhancedScopeData, entityID);
  }
  if (entityType === APIEntityType.COMPONENT) {
    return componentUtils.isExternallyManaged(enhancedScopeData, entityID);
  }
  return false;
};

const isFieldExternallyManagedForEntityType = (
  enhancedScopeData: EnhancedScopeData,
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  fieldName: string,
  workspaceId: ArdoqId
): boolean => {
  const integrations =
    enhancedScopeData.workspacesById[workspaceId]?.integrations ?? [];
  const managedFieldNames = integrations.flatMap(integration =>
    entityType === APIEntityType.REFERENCE
      ? integration.referenceFields
      : integration.componentFields
  );
  return managedFieldNames.includes(fieldName);
};

const isEditingAcrossMultipleWorkspaces = (
  enhancedScopeData: EnhancedScopeData,
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  entityIDs: ArdoqId[]
) =>
  uniq(
    entityIDs.map(
      entityType === APIEntityType.COMPONENT
        ? compId => componentUtils.getWorkspaceId(enhancedScopeData, compId)
        : refId => referenceUtils.getWorkspaceId(enhancedScopeData, refId)
    )
  ).length > 1;

const isMultiEditing = (entityIDs: ArdoqId[]) => entityIDs.length > 1;

export const getPrimaryButtonLabel = (
  isCreatingNewEntity: boolean,
  isCalculatedField: boolean,
  isCalculatedFieldWithoutQuery: boolean,
  i18nLocale: I18nLocale
) => {
  if (!isCreatingNewEntity && isCalculatedFieldWithoutQuery) {
    return t(
      i18nLocale,
      'propertiesEditor:buttons.saveAndEditQuery',
      'Save & edit query'
    );
  }
  if (!isCreatingNewEntity) {
    return t(i18nLocale, 'propertiesEditor:buttons.save', 'Save');
  }
  if (isCalculatedField) {
    return t(
      i18nLocale,
      'propertiesEditor:buttons.createAndEditQuery',
      'Create & edit query'
    );
  }
  return t(i18nLocale, 'propertiesEditor:buttons.create', 'Create');
};

export const isAnyEntityLocked = (
  entityType: APIEntityType,
  entityIDs: ArdoqId[],
  enhancedScopeData: EnhancedScopeData
) => {
  if (
    entityType !== APIEntityType.COMPONENT &&
    entityType !== APIEntityType.REFERENCE
  ) {
    return false;
  }
  return lockOperations.isAnyLocked(
    entityIDs.map(
      entityID => getEntityById(entityType, entityID, enhancedScopeData)?.lock
    ),
    getFeatureContext()
  );
};

export const getLockedMessage = (
  isAnyLocked: boolean,
  i18nLocale: I18nLocale
) => (isAnyLocked ? getLockedMessageText(i18nLocale) : '');

export const getMultiEditingMessage = (
  entityType: APIEntityType,
  entityIDs: ArdoqId[],
  enhancedScopeData: EnhancedScopeData,
  i18nLocale: I18nLocale,
  isEditingComponentStyle?: boolean
) => {
  if (!isMultiEditing(entityIDs) && isEditingComponentStyle) {
    const name = enhancedScopeData.components.find(component =>
      entityIDs.includes(component._id)
    )?.name;
    return t(
      i18nLocale,
      'propertiesEditor:multiEditing.singleComponent',
      { name: name ?? '' },
      'Editing 1 component ({{name}})'
    );
  }
  if (!isMultiEditing(entityIDs)) {
    return;
  }
  const charLimit = 300;
  if (entityType === APIEntityType.COMPONENT) {
    const componentNames = enhancedScopeData.components
      .filter(component => entityIDs.includes(component._id))
      .map(component => component.name);
    const message = t(
      i18nLocale,
      'propertiesEditor:multiEditing.components',
      { count: entityIDs.length, names: componentNames.join(', ') },
      'Editing {{count}} components ({{names}})'
    );
    return truncate(message, { length: charLimit });
  }
  if (entityType === APIEntityType.REFERENCE) {
    const referenceNames = enhancedScopeData.references
      .filter(reference => entityIDs.includes(reference._id))
      .map(toSourceTargetIdTuple)
      .map(sourceTargetIdTupleToNameTuple(enhancedScopeData.componentsById))
      .map(componentNames => componentNames.join(' -> '));
    const message = t(
      i18nLocale,
      'propertiesEditor:multiEditing.references',
      { count: entityIDs.length, names: referenceNames.join(', ') },
      'Editing {{count}} references ({{names}})'
    );
    return truncate(message, { length: charLimit });
  }
};

export const getSidebarInstanceId = (
  entityType: APIEntityType,
  entityIDs: ArdoqId[],
  enhancedScopeData: EnhancedScopeData,
  model?: ArdoqId
) => {
  if (entityType === APIEntityType.WORKSPACE) {
    const entityID = entityIDs[0];
    const workspace = enhancedScopeData.workspacesById[entityID];
    const version = workspace._version;
    return `${entityType}-${entityID}-${version}`;
  }
  return entityIDs
    .map(entityID => {
      const entity = getEntityById(
        entityType,
        entityID,
        enhancedScopeData,
        model
      );
      if (!entity) return;
      // @ts-expect-error _version isn't a property on APIComponentType, which entity might be.
      const entityVersion = entity._version;
      if (!entityVersion && model) {
        const version = getEntityById(
          APIEntityType.MODEL,
          model,
          enhancedScopeData
        )?._version;
        return `${entityType}-${entityID}-${version}`;
      }
      return `${entityType}-${entityID}-${entityVersion}`;
    })
    .join('-');
};

export const getHeaderTitle = (
  entityType: EditableEntityType,
  isCreatingNewEntity: boolean,
  i18nLocale: I18nLocale,
  isEditingComponentStyle?: boolean
) => {
  if (isEditingComponentStyle) {
    return t(
      i18nLocale,
      'propertiesEditor:header.editComponentStyle',
      'Edit component style properties'
    );
  }
  if (entityType === APIEntityType.COMPONENT) {
    if (isCreatingNewEntity) {
      return t(
        i18nLocale,
        'propertiesEditor:header.createComponent',
        'Create component'
      );
    }
    return t(
      i18nLocale,
      'propertiesEditor:header.editComponent',
      'Edit component properties'
    );
  }
  if (isCreatingNewEntity) {
    return t(
      i18nLocale,
      'propertiesEditor:header.createReference',
      'Create reference'
    );
  }
  return t(
    i18nLocale,
    'propertiesEditor:header.editReference',
    'Edit reference properties'
  );
};

export const canShowTags = (
  entityType: APIEntityType,
  isEditingComponentStyle?: boolean
) =>
  !isEditingComponentStyle &&
  (entityType === APIEntityType.COMPONENT ||
    entityType === APIEntityType.REFERENCE);

export const canDelete = (
  entityType: APIEntityType,
  isCreatingNewEntity: boolean,
  isLocked: boolean,
  isEditingComponentStyle?: boolean
) =>
  !isCreatingNewEntity &&
  !isLocked &&
  !isEditingComponentStyle &&
  (entityType === APIEntityType.COMPONENT ||
    entityType === APIEntityType.REFERENCE);

export const isEntityCalculatedField = (
  entityType: APIEntityType,
  entityID: ArdoqId,
  enhancedScopeData: EnhancedScopeData
) => {
  if (entityType !== APIEntityType.FIELD) {
    return false;
  }
  const field = enhancedScopeData.fieldsById[entityID];

  return field ? fieldOps.isCalculatedField(field) : false;
};

export const isEntityCalculatedFieldWithoutQuery = (
  entityType: APIEntityType,
  entityID: ArdoqId,
  enhancedScopeData: EnhancedScopeData
) => {
  if (entityType !== APIEntityType.FIELD) {
    return false;
  }
  const field = enhancedScopeData.fieldsById[entityID];

  return field ? fieldOps.isCalculatedFieldWithoutQuery(field) : false;
};

export const isEntityDateRangeField = (
  entityType: APIEntityType,
  entityID: ArdoqId,
  enhancedScopeData: EnhancedScopeData
) => {
  if (entityType !== APIEntityType.FIELD) {
    return false;
  }
  const field = enhancedScopeData.fieldsById[entityID];

  return field ? isDateRangeFieldType(field.type) : false;
};

const isPropertyDisabled = (
  propertyName: string,
  entityIDs: ArdoqId[],
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  isExternallyManaged: boolean,
  propertiesNotEditableAcrossMultipleWorkspaces: string[],
  enhancedScopeData: EnhancedScopeData,
  canEditEntity: boolean
) => {
  return (
    !canEditEntity ||
    isExternallyManaged ||
    (isEditingAcrossMultipleWorkspaces(
      enhancedScopeData,
      entityType,
      entityIDs
    ) &&
      propertiesNotEditableAcrossMultipleWorkspaces.includes(propertyName))
  );
};

const getDisabledMessage = ({
  isDisabled,
  isExternallyManaged,
  isCalculated,
  isLocallyDerived,
  isConfidential,
  i18nLocale,
}: {
  isDisabled: boolean;
  isExternallyManaged: boolean;
  isCalculated?: boolean;
  isLocallyDerived?: boolean;
  isConfidential?: boolean;
  i18nLocale: I18nLocale;
}) => {
  if (isConfidential) {
    return getConfidentialFieldEditMessage(i18nLocale);
  }
  if (isCalculated) {
    return getCalculatedFieldEditMessage(i18nLocale);
  }
  if (isLocallyDerived) {
    return getLocallyDerivedFieldEditMessage(i18nLocale);
  }
  if (isExternallyManaged) {
    return getManagedFieldEditMessage(i18nLocale);
  }
  if (isDisabled) {
    return getCrossWorkspaceEditMessage(i18nLocale);
  }
};

const getValue = (
  attributes: APIComponentAttributes | APIReferenceAttributes,
  field: APIFieldAttributes,
  isCreatingNewEntity: boolean
) => {
  if (
    !isCreatingNewEntity ||
    field.type === APIFieldType.LIST ||
    field.type === APIFieldType.SELECT_MULTIPLE_LIST
  ) {
    return attributes[field.name];
  }
  return hasOwnProperty(attributes, field.name)
    ? attributes[field.name]
    : field.defaultValue;
};

const getNumberDisplayValue = (
  value: unknown,
  numberFormatOptions: string | null | undefined,
  locale: Locale
): string | undefined => {
  return typeof value === 'number' && numberFormatOptions
    ? formatNumber(value, numberFormatOptions, locale)
    : undefined;
};

const getNumberEditValue = (
  value: unknown,
  numberFormatOptions: string | null | undefined
): number | undefined => {
  return typeof value === 'number' &&
    numberFormatOptions === NUMBER_FORMAT_PERCENTAGE
    ? percentageStoredValueToEditValue(value)
    : (value as number);
};

const getEntityModelTypeKey = (
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  entityID: ArdoqId,
  enhancedScopeData: EnhancedScopeData
) => {
  const { model, typeId } = getEntityModelAndTypeID(
    entityType,
    entityID,
    enhancedScopeData
  );
  if (!model || !typeId) {
    return null;
  }
  return getModelTypeDictionaryKey(model, typeId);
};

const attributeNameToDefaultPropertyObject =
  (
    entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
    entityID: ArdoqId,
    entityIDs: ArdoqId[],
    enhancedScopeData: EnhancedScopeData,
    attributeTypeMap: Map<string, FieldType>,
    optionGetterMap: DefaultPropertyOptionGetterMap,
    propertiesNotEditableAcrossMultipleWorkspaces: string[],
    dirtyAttributes: DirtyAttributes,
    permissionContext: PermissionContext,
    isCreatingNewEntity: boolean,
    locale: Locale,
    i18nLocale: I18nLocale,
    numberFormatByFieldName: Record<string, string | null>,
    refTypesSortedByMostUsed?: APIReferenceType[] | null,
    intendedRefTypes?: IntendedRefTypes,
    isEnforcementEnabled?: boolean
  ): ((attributeName: string) => EditorProperty | null) =>
  (attributeName: string) => {
    const type =
      defaultAttributesMap.get(attributeName) ??
      attributeTypeMap.get(attributeName);
    if (!type) {
      return null;
    }
    const label = getFieldLabel({
      fieldName: attributeName,
      enhancedScopeData,
      entityType,
    });
    const rootWorkspaceId = readRawValue(
      entityType,
      entityID,
      'rootWorkspace',
      enhancedScopeData
    );
    const isEntityExternallyManaged = isManaged(
      entityType,
      entityID,
      enhancedScopeData
    );
    const isFieldExternallyManaged = isFieldExternallyManagedForEntityType(
      enhancedScopeData,
      entityType,
      attributeName,
      rootWorkspaceId
    );
    const activeScenarioState = getActiveScenarioState();

    let canEditEntity = workspaceAccessControlInterface.canEditWorkspace(
      permissionContext,
      rootWorkspaceId,
      activeScenarioState
    );
    if (activeScenarioOperations.isInScenarioMode(activeScenarioState)) {
      canEditEntity = scenarioAccessControlInterface.canEditActiveScenario(
        permissionContext,
        activeScenarioState
      );
    } else if (entityType === APIEntityType.COMPONENT && !canEditEntity) {
      let componentData: APIComponentAttributes | null =
        enhancedScopeData.componentsById[entityID];
      if (isCreatingNewEntity) {
        componentData = componentData?.parent
          ? enhancedScopeData.componentsById[componentData.parent]
          : null;
      }
      canEditEntity =
        !!componentData &&
        componentAccessControlOperation.canEditComponent({
          permissionContext,
          component: componentData,
          featureContext: getFeatureContext(),
        });
    } else if (entityType === APIEntityType.REFERENCE) {
      const referenceData = enhancedScopeData.referencesById[entityID];
      canEditEntity =
        referenceData &&
        referenceAccessControlOperation.canEditReference({
          permissionContext,
          reference: referenceData,
          sourceComponent:
            enhancedScopeData.componentsById[referenceData.source],
          featureContext: getFeatureContext(),
        });
    }

    const isExternallyManaged =
      isEntityExternallyManaged && isFieldExternallyManaged;
    const externallyManagedInfo = {
      isExternallyManaged,
      externallyManagedWorkspaceId: rootWorkspaceId,
    };
    const isDisabled = isPropertyDisabled(
      attributeName,
      entityIDs,
      entityType,
      isExternallyManaged,
      propertiesNotEditableAcrossMultipleWorkspaces,
      enhancedScopeData,
      canEditEntity
    );
    const value = readRawValue(
      entityType,
      entityID,
      attributeName,
      enhancedScopeData
    );
    const isDirty = dirtyAttributes.has(attributeName);

    const displayTextError = getDisplayTextErrorMessage(
      entityType,
      attributeName,
      type,
      value,
      entityID,
      intendedRefTypes,
      enhancedScopeData
    );
    const errorMessages = getValidationErrorMessages(type, value);
    if (displayTextError) {
      errorMessages.push(displayTextError);
    }

    const availableOptions = optionGetterMap.get(type)?.(
      entityID,
      enhancedScopeData,
      permissionContext,
      {
        refTypesSortedByMostUsed,
        intendedRefTypes,
        isEnforcementEnabled,
        i18nLocale,
      }
    );

    if (type === FieldType.TYPE_ID_REFERENCE && availableOptions) {
      const validationError = selectedValueExistInOptionsValidator(
        value as number | null,
        availableOptions
      );

      if (validationError) {
        errorMessages.push(validationError);
      }
    }

    // Check if this displayText field is conditionally required (regardless of current value)
    const isDisplayTextRequired = isDisplayTextRequiredForReference(
      entityType,
      attributeName,
      type,
      entityID,
      intendedRefTypes,
      enhancedScopeData
    );

    const fieldDescription = isDisplayTextRequired
      ? t(
          i18nLocale,
          'propertiesEditor:errors.implicitRequiresDisplayText',
          'When using the Implicit reference type, the display text is required.'
        )
      : undefined;
    const indicator = isDisplayTextRequired ? 'required' : undefined;

    return {
      type,
      name: attributeName,
      indicator,
      label,
      value:
        type === FieldType.NUMBER
          ? getNumberEditValue(
              value,
              numberFormatByFieldName[attributeName] ?? null
            )
          : value,
      description: fieldDescription,
      isDirty,
      errorMessages,
      options: availableOptions,
      isDisabled,
      disabledMessage: getDisabledMessage({
        isDisabled,
        isExternallyManaged,
        i18nLocale,
      }),
      ...(isExternallyManaged ? externallyManagedInfo : {}),
      ...(type === FieldType.DESCRIPTION
        ? {
            workspaceId: rootWorkspaceId,
            ...(entityType === APIEntityType.COMPONENT
              ? {
                  // These are used by the component description generator
                  componentName:
                    enhancedScopeData.componentsById[entityID].name,
                  componentTypeName:
                    enhancedScopeData.componentsById[entityID].type,
                  workspaceName:
                    enhancedScopeData.workspacesById[rootWorkspaceId].name,
                  entityType,
                }
              : {}),
          }
        : {}),
      ...(type === FieldType.NUMBER
        ? {
            numberFormatOptions: numberFormatByFieldName[attributeName] ?? null,
            displayValue: getNumberDisplayValue(
              value,
              numberFormatByFieldName[attributeName] ?? null,
              locale
            ),
          }
        : {}),
    };
  };

const fieldAttributesToCustomPropertyObject =
  (
    entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
    attributes: APIComponentAttributes | APIReferenceAttributes,
    enhancedScopeData: EnhancedScopeData,
    isEntityExternallyManaged: boolean,
    dirtyAttributes: DirtyAttributes,
    isCreatingNewEntity: boolean,
    optionGetterMap: CustomPropertyOptionGetterMap,
    permissionContext: PermissionContext,
    locale: Locale,
    i18nLocale: I18nLocale
  ): ((field: APIFieldAttributes) => EditorProperty | undefined) =>
  (field: APIFieldAttributes) => {
    const isCalculated = fieldOps.isCalculatedField(field);
    const isLocallyDerived = fieldOps.isLocallyDerivedField(field);
    if (isCalculated) {
      return;
    }
    const type = fieldTypeMap.get(field.type)!;
    const label = getFieldLabel({
      fieldName: field.name,
      enhancedScopeData,
      entityType,
    });
    const rootWorkspaceId = attributes.rootWorkspace;
    const isFieldExternallyManaged = isFieldExternallyManagedForEntityType(
      enhancedScopeData,
      entityType,
      field.name,
      rootWorkspaceId
    );
    const isExternallyManaged =
      isEntityExternallyManaged && isFieldExternallyManaged;
    const externallyManagedInfo = {
      isExternallyManaged,
      externallyManagedWorkspaceId: rootWorkspaceId,
    };

    const isConfidential =
      entityType === APIEntityType.COMPONENT &&
      !permissionsOperations.canSeeConfidentialFieldByName(
        permissionContext,
        field.name,
        rootWorkspaceId
      );

    const activeScenarioState = getActiveScenarioState();
    let hasNoEditAccess = !workspaceAccessControlInterface.canEditWorkspace(
      permissionContext,
      rootWorkspaceId,
      activeScenarioState
    );
    if (activeScenarioOperations.isInScenarioMode(activeScenarioState)) {
      hasNoEditAccess = !scenarioAccessControlInterface.canEditActiveScenario(
        permissionContext,
        activeScenarioState
      );
    } else if (hasNoEditAccess) {
      if (entityType === APIEntityType.COMPONENT) {
        let componentData: null | APIComponentAttributes = null;
        if (!isCreatingNewEntity) {
          if (enhancedScopeData.componentsById[attributes._id]) {
            componentData = attributes as APIComponentAttributes;
          }
        } else {
          componentData = attributes.parent
            ? enhancedScopeData.componentsById[attributes.parent]
            : null;
        }
        if (componentData) {
          hasNoEditAccess = !componentAccessControlOperation.canEditField({
            component: componentData,
            fieldId: field._id,
            permissionContext,
            featureContext: getFeatureContext(),
          });
        }
      } else if (entityType === APIEntityType.REFERENCE) {
        hasNoEditAccess = !referenceAccessControlOperation.canEditReference({
          reference: attributes as APIReferenceAttributes,
          sourceComponent:
            enhancedScopeData.componentsById[
              (attributes as APIReferenceAttributes).source
            ],
          permissionContext,
          featureContext: getFeatureContext(),
        });
      }
    }
    const isDisabled =
      isExternallyManaged ||
      isCalculated ||
      isLocallyDerived ||
      hasNoEditAccess ||
      isConfidential;
    const value = getValue(attributes, field, isCreatingNewEntity);
    const isDirty = dirtyAttributes.has(field.name);
    const errorMessages = getValidationErrorMessages(type, value);

    return {
      type,
      name: field.name,
      label,
      description: field.description,
      value:
        type === FieldType.NUMBER
          ? getNumberEditValue(value, field.numberFormatOptions)
          : value,
      isDirty,
      errorMessages,
      options: optionGetterMap.get(type)?.(field),
      isDisabled,
      disabledMessage: getDisabledMessage({
        isDisabled,
        isExternallyManaged,
        isCalculated,
        isLocallyDerived,
        isConfidential,
        i18nLocale,
      }),
      ...(isExternallyManaged ? externallyManagedInfo : {}),
      ...(type === FieldType.NUMBER
        ? {
            numberFormatOptions: field.numberFormatOptions,
            displayValue: getNumberDisplayValue(
              value,
              field.numberFormatOptions,
              locale
            ),
          }
        : {}),
    };
  };

const getDefaultEntityProperties = (
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  entityID: ArdoqId,
  entityIDs: ArdoqId[],
  enhancedScopeData: EnhancedScopeData,
  attributeNames: string[],
  attributeTypeMap: Map<string, FieldType>,
  defaultPropertyOptionGetterMap: DefaultPropertyOptionGetterMap,
  propertiesNotEditableAcrossMultipleWorkspaces: string[],
  dirtyAttributes: DirtyAttributes,
  permissionContext: PermissionContext,
  isCreatingNewEntity: boolean,
  locale: Locale,
  i18nLocale: I18nLocale,
  numberFormatByFieldName: Record<string, string | null>,
  refTypesSortedByMostUsed?: APIReferenceType[] | null,
  intendedRefTypes?: IntendedRefTypes,
  isEnforcementEnabled?: boolean
): EditorProperty[] => {
  const properties = attributeNames
    .map(
      attributeNameToDefaultPropertyObject(
        entityType,
        entityID,
        entityIDs,
        enhancedScopeData,
        attributeTypeMap,
        defaultPropertyOptionGetterMap,
        propertiesNotEditableAcrossMultipleWorkspaces,
        dirtyAttributes,
        permissionContext,
        isCreatingNewEntity,
        locale,
        i18nLocale,
        numberFormatByFieldName,
        refTypesSortedByMostUsed,
        intendedRefTypes,
        isEnforcementEnabled
      )
    )
    .filter(isTruthy);

  if (entityType === APIEntityType.REFERENCE) {
    const hasErrors = (properties[0].errorMessages as string[]).length > 0;
    return [
      properties[0],
      !hasErrors &&
        getTripleWarningProperty(entityID, enhancedScopeData, intendedRefTypes),
      ...properties.slice(1),
    ].filter(isTruthy);
  }

  return properties;
};

const getDefaultMultiEntityProperties = (
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  entityIDs: ArdoqId[],
  enhancedScopeData: EnhancedScopeData,
  attributeNames: string[],
  attributeTypeMap: Map<string, FieldType>,
  defaultPropertyOptionGetterMap: DefaultPropertyOptionGetterMap,
  propertiesNotEditableAcrossMultipleWorkspaces: string[],
  dirtyAttributes: DirtyAttributes,
  permissionContext: PermissionContext,
  locale: Locale,
  i18nLocale: I18nLocale,
  numberFormatByFieldName: Record<string, string | null>,
  refTypesSortedByMostUsed?: APIReferenceType[] | null,
  intendedRefTypes?: IntendedRefTypes,
  isEnforcementEnabled?: boolean
): EditorProperty[] => {
  const propertiesPerEntity = entityIDs.map(entityID =>
    getDefaultEntityProperties(
      entityType,
      entityID,
      entityIDs,
      enhancedScopeData,
      attributeNames,
      attributeTypeMap,
      defaultPropertyOptionGetterMap,
      propertiesNotEditableAcrossMultipleWorkspaces,
      dirtyAttributes,
      permissionContext,
      false,
      locale,
      i18nLocale,
      numberFormatByFieldName,
      refTypesSortedByMostUsed,
      intendedRefTypes,
      isEnforcementEnabled
    )
  );
  return getMergedMultiEntityProperties(propertiesPerEntity, i18nLocale);
};

const getCustomEntityProperties = (
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  entityID: ArdoqId,
  _activeScenarioState: ScenarioModeState,
  enhancedScopeData: EnhancedScopeData,
  dirtyAttributes: DirtyAttributes,
  isCreatingNewEntity: boolean,
  customPropertyOptionGetterMap: CustomPropertyOptionGetterMap,
  permissionContext: PermissionContext,
  locale: Locale,
  i18nLocale: I18nLocale
): EditorProperty[] => {
  const attributes = getEntityById(entityType, entityID, enhancedScopeData);
  if (!attributes) {
    return [];
  }
  const key = getEntityModelTypeKey(entityType, entityID, enhancedScopeData);
  if (!key) {
    return [];
  }
  const entityFieldDict = enhancedScopeData.fieldsByModelIdAndTypeId[key];
  if (!entityFieldDict) {
    return [];
  }
  const entityFields = Object.values(entityFieldDict);

  return entityFields
    .map(
      fieldAttributesToCustomPropertyObject(
        entityType,
        attributes,
        enhancedScopeData,
        isManaged(entityType, entityID, enhancedScopeData),
        dirtyAttributes,
        isCreatingNewEntity,
        customPropertyOptionGetterMap,
        permissionContext,
        locale,
        i18nLocale
      )
    )
    .filter(isTruthy);
};

export const getCustomMultiEntityProperties = (
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  entityIDs: ArdoqId[],
  activeScenarioState: ScenarioModeState,
  enhancedScopeData: EnhancedScopeData,
  dirtyAttributes: DirtyAttributes,
  isCreatingNewEntity: boolean,
  customPropertyOptionGetterMap: CustomPropertyOptionGetterMap,
  permissionContext: PermissionContext,
  locale: Locale,
  i18nLocale: I18nLocale
): EditorProperty[] => {
  const propertiesPerEntity = entityIDs.map(entityID => {
    return getCustomEntityProperties(
      entityType,
      entityID,
      activeScenarioState,
      enhancedScopeData,
      dirtyAttributes,
      isCreatingNewEntity,
      customPropertyOptionGetterMap,
      permissionContext,
      locale,
      i18nLocale
    );
  });
  const uniqueModelTypeKeys = new Set(
    entityIDs
      .map(entityID =>
        getEntityModelTypeKey(entityType, entityID, enhancedScopeData)
      )
      .filter(isTruthy)
  );
  const fieldNamesPerType = Array.from(uniqueModelTypeKeys).map(
    modelTypeKey => {
      // It can be that no fields are defined yet for a given type.
      const fields =
        enhancedScopeData.fieldsByModelIdAndTypeId[modelTypeKey] ?? {};
      return Object.keys(fields);
    }
  );
  const commonPropertyNames = new Set(intersection(...fieldNamesPerType));
  return getMergedCustomMultiEntityProperties(
    propertiesPerEntity,
    commonPropertyNames,
    i18nLocale
  );
};

const getDefaultProperties = (
  entityType: APIEntityType,
  entityIDs: ArdoqId[],
  enhancedScopeData: EnhancedScopeData,
  dirtyAttributes: DirtyAttributes,
  permissionContext: PermissionContext,
  isCreatingNewEntity: boolean,
  locale: Locale,
  i18nLocale: I18nLocale,
  numberFormatByFieldName: Record<string, string | null>,
  refTypesSortedByMostUsed?: APIReferenceType[] | null,
  intendedRefTypes?: IntendedRefTypes,
  isEnforcementEnabled?: boolean
): EditorProperty[] => {
  switch (entityType) {
    case APIEntityType.COMPONENT: {
      if (isMultiEditing(entityIDs)) {
        return getDefaultMultiEntityProperties(
          entityType,
          entityIDs,
          enhancedScopeData,
          defaultMultiComponentAttributeNames,
          componentAttributesMap,
          defaultComponentPropertyOptionGetterMap,
          componentPropertiesNotEditableAcrossMultipleWorkspaces,
          dirtyAttributes,
          permissionContext,
          locale,
          i18nLocale,
          numberFormatByFieldName
        );
      }
      return getDefaultEntityProperties(
        entityType,
        entityIDs[0],
        entityIDs,
        enhancedScopeData,
        defaultComponentAttributeNames,
        componentAttributesMap,
        defaultComponentPropertyOptionGetterMap,
        componentPropertiesNotEditableAcrossMultipleWorkspaces,
        dirtyAttributes,
        permissionContext,
        isCreatingNewEntity,
        locale,
        i18nLocale,
        numberFormatByFieldName
      );
    }
    case APIEntityType.REFERENCE: {
      if (isMultiEditing(entityIDs)) {
        return getDefaultMultiEntityProperties(
          entityType,
          entityIDs,
          enhancedScopeData,
          defaultMultiReferenceAttributeNames.filter(name =>
            entityIDs.every(entityID =>
              isCardinalityAttributeAllowed(entityID, enhancedScopeData)(name)
            )
          ),
          referenceAttributesMap,
          defaultReferencePropertyOptionGetterMap,
          referencePropertiesNotEditableAcrossMultipleWorkspaces,
          dirtyAttributes,
          permissionContext,
          locale,
          i18nLocale,
          numberFormatByFieldName,
          refTypesSortedByMostUsed,
          intendedRefTypes,
          isEnforcementEnabled
        );
      }
      return getDefaultEntityProperties(
        entityType,
        entityIDs[0],
        entityIDs,
        enhancedScopeData,
        defaultReferenceAttributeNames.filter(
          isCardinalityAttributeAllowed(entityIDs[0], enhancedScopeData)
        ),
        referenceAttributesMap,
        defaultReferencePropertyOptionGetterMap,
        referencePropertiesNotEditableAcrossMultipleWorkspaces,
        dirtyAttributes,
        permissionContext,
        isCreatingNewEntity,
        locale,
        i18nLocale,
        numberFormatByFieldName,
        refTypesSortedByMostUsed,
        intendedRefTypes,
        isEnforcementEnabled
      );
    }
    default:
      // TODO implement other entity types
      return [];
  }
};

const getComponentPropertyGroups = (
  entityIDs: ArdoqId[],
  activeScenarioState: ScenarioModeState,
  enhancedScopeData: EnhancedScopeData,
  dirtyAttributes: DirtyAttributes,
  isCreatingNewEntity: boolean,
  permissionContext: PermissionContext,
  locale: Locale,
  i18nLocale: I18nLocale,
  numberFormatByFieldName: Record<string, string | null>
): PropertyGroup[] => {
  const fieldsTitle = t(i18nLocale, 'propertiesEditor:groups.fields', 'Fields');
  if (isMultiEditing(entityIDs)) {
    return [
      {
        properties: getDefaultProperties(
          APIEntityType.COMPONENT,
          entityIDs,
          enhancedScopeData,
          dirtyAttributes,
          permissionContext,
          false,
          locale,
          i18nLocale,
          numberFormatByFieldName
        ),
      },
      {
        title: fieldsTitle,
        properties: getCustomMultiEntityProperties(
          APIEntityType.COMPONENT,
          entityIDs,
          activeScenarioState,
          enhancedScopeData,
          dirtyAttributes,
          isCreatingNewEntity,
          customComponentPropertyOptionGetterMap,
          permissionContext,
          locale,
          i18nLocale
        ),
      },
    ];
  }
  return [
    {
      properties: getDefaultProperties(
        APIEntityType.COMPONENT,
        entityIDs,
        enhancedScopeData,
        dirtyAttributes,
        permissionContext,
        isCreatingNewEntity,
        locale,
        i18nLocale,
        numberFormatByFieldName
      ),
    },
    {
      title: fieldsTitle,
      properties: getCustomEntityProperties(
        APIEntityType.COMPONENT,
        entityIDs[0],
        activeScenarioState,
        enhancedScopeData,
        dirtyAttributes,
        isCreatingNewEntity,
        customComponentPropertyOptionGetterMap,
        permissionContext,
        locale,
        i18nLocale
      ),
    },
  ];
};
const getComponentStylePropertyGroups = (
  entityIDs: ArdoqId[],
  activeScenarioState: ScenarioModeState,
  enhancedScopeData: EnhancedScopeData,
  dirtyAttributes: DirtyAttributes,
  permissionContext: PermissionContext,
  i18nLocale: I18nLocale,
  imageOptions: ComponentStyleImageOption[]
): PropertyGroup[] => {
  return [
    {
      title: t(i18nLocale, 'propertiesEditor:groups.style', 'Style'),
      properties: getDefaultComponentStyleProperties(
        entityIDs[0],
        activeScenarioState,
        enhancedScopeData,
        dirtyAttributes,
        permissionContext,
        i18nLocale,
        imageOptions
      ),
    },
  ];
};
const getReferencePropertyGroups = (
  entityIDs: ArdoqId[],
  activeScenarioState: ScenarioModeState,
  enhancedScopeData: EnhancedScopeData,
  dirtyAttributes: DirtyAttributes,
  isCreatingNewEntity: boolean,
  permissionContext: PermissionContext,
  locale: Locale,
  i18nLocale: I18nLocale,
  numberFormatByFieldName: Record<string, string | null>,
  refTypesSortedByMostUsed?: APIReferenceType[] | null,
  intendedRefTypes?: IntendedRefTypes,
  isEnforcementEnabled?: boolean
): PropertyGroup[] => {
  const fieldsTitle = t(i18nLocale, 'propertiesEditor:groups.fields', 'Fields');
  if (isMultiEditing(entityIDs)) {
    return [
      {
        properties: getDefaultProperties(
          APIEntityType.REFERENCE,
          entityIDs,
          enhancedScopeData,
          dirtyAttributes,
          permissionContext,
          isCreatingNewEntity,
          locale,
          i18nLocale,
          numberFormatByFieldName,
          refTypesSortedByMostUsed,
          intendedRefTypes,
          isEnforcementEnabled
        ),
      },
      {
        title: fieldsTitle,
        properties: getCustomMultiEntityProperties(
          APIEntityType.REFERENCE,
          entityIDs,
          activeScenarioState,
          enhancedScopeData,
          dirtyAttributes,
          isCreatingNewEntity,
          customReferencePropertyOptionGetterMap,
          permissionContext,
          locale,
          i18nLocale
        ),
      },
    ];
  }
  return [
    {
      properties: getDefaultProperties(
        APIEntityType.REFERENCE,
        entityIDs,
        enhancedScopeData,
        dirtyAttributes,
        permissionContext,
        isCreatingNewEntity,
        locale,
        i18nLocale,
        numberFormatByFieldName,
        refTypesSortedByMostUsed,
        intendedRefTypes,
        isEnforcementEnabled
      ),
    },
    {
      title: fieldsTitle,
      properties: getCustomEntityProperties(
        APIEntityType.REFERENCE,
        entityIDs[0],
        activeScenarioState,
        enhancedScopeData,
        dirtyAttributes,
        isCreatingNewEntity,
        customReferencePropertyOptionGetterMap,
        permissionContext,
        locale,
        i18nLocale
      ),
    },
  ];
};

export const getPropertyGroups = (
  entityType: APIEntityType,
  entityIDs: ArdoqId[],
  activeScenarioState: ScenarioModeState,
  enhancedScopeData: EnhancedScopeData,
  dirtyAttributes: DirtyAttributes,
  isCreatingNewEntity: boolean,
  permissionContext: PermissionContext,
  locale: Locale,
  i18nLocale: I18nLocale,
  numberFormatByFieldName: Record<string, string | null>,
  imageOptions: ComponentStyleImageOption[],
  _model?: ArdoqId,
  isEditingComponentStyle?: boolean,
  refTypesSortedByMostUsed?: APIReferenceType[] | null,
  intendedRefTypes?: IntendedRefTypes,
  isEnforcementEnabled?: boolean
): PropertyGroup[] => {
  switch (entityType) {
    case APIEntityType.COMPONENT: {
      if (isEditingComponentStyle) {
        return getComponentStylePropertyGroups(
          entityIDs,
          activeScenarioState,
          enhancedScopeData,
          dirtyAttributes,
          permissionContext,
          i18nLocale,
          imageOptions
        );
      }
      return getComponentPropertyGroups(
        entityIDs,
        activeScenarioState,
        enhancedScopeData,
        dirtyAttributes,
        isCreatingNewEntity,
        permissionContext,
        locale,
        i18nLocale,
        numberFormatByFieldName
      );
    }
    case APIEntityType.REFERENCE: {
      return getReferencePropertyGroups(
        entityIDs,
        activeScenarioState,
        enhancedScopeData,
        dirtyAttributes,
        isCreatingNewEntity,
        permissionContext,
        locale,
        i18nLocale,
        numberFormatByFieldName,
        refTypesSortedByMostUsed,
        intendedRefTypes,
        isEnforcementEnabled
      );
    }
    default:
      // TODO implement other entity types
      return [];
  }
};

export const getTags = (
  entityType: APIEntityType,
  entityIDs: ArdoqId[],
  enhancedScopeData: EnhancedScopeData,
  permissionContext: PermissionContext,
  activeScenarioState: ScenarioModeState,
  isCreatingNewEntity: boolean
) => {
  switch (entityType) {
    case APIEntityType.COMPONENT:
    case APIEntityType.REFERENCE: {
      return getEntityTags(
        entityType,
        entityIDs,
        enhancedScopeData,
        permissionContext,
        activeScenarioState,
        isCreatingNewEntity
      );
    }
    default:
      // TODO implement other entity types
      return { value: [], options: [] };
  }
};

/*
 *  -> Attributes
 */

export const attributesFromProperties = (propertyGroups: PropertyGroup[]) => {
  return Object.fromEntries(
    [
      ...propertyGroups.flatMap(propertyGroup =>
        splitDateRangeProperties(propertyGroup.properties)
      ),
    ].map(property => {
      const { name, value, type, numberFormatOptions } = property;

      // Convert percentage values from edit format (25) to stored format (0.25)
      const storedValue =
        type === FieldType.NUMBER &&
        numberFormatOptions === NUMBER_FORMAT_PERCENTAGE &&
        typeof value === 'number'
          ? percentageEditValueToStoredValue(value)
          : value;

      return [name, storedValue];
    })
  );
};

export const getSaveButtonDisabledState = (
  propertyGroups: PropertyGroup[],
  isCreatingNewEntity: boolean,
  allCurrentProperties: EditableEntityPropertiesToCompare[],
  allOriginalProperties: EditableEntityPropertiesToCompare[],
  isMultiEditing = false
) => {
  const hasErrors = propertyGroups
    .flatMap(({ properties }) => properties)
    .flatMap(({ errorMessages }) => errorMessages ?? [])
    .some(currentErrorMessage => currentErrorMessage);
  if (isCreatingNewEntity && !hasErrors) return false;

  if (isMultiEditing) {
    // For bulk editing, check if ANY entity has changes
    const hasAnyChanges = allCurrentProperties.some((currentProps, index) => {
      const originalProps = allOriginalProperties[index];
      return !isEqual(currentProps, originalProps);
    });

    return hasErrors || !hasAnyChanges;
  }

  // For single entity editing, use the original logic (first element)
  return (
    hasErrors || isEqual(allCurrentProperties[0], allOriginalProperties[0])
  );
};
